import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FaGift, FaSearch, FaArrowLeft, FaHeart, FaUserFriends } from 'react-icons/fa';
import GiftModal from '../../components/features/gifts/GiftModal';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../services/api';
import './GiftsPage.css';


const GiftsPage = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useSelector((state) => state.auth);
  const { isDark } = useTheme();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('loves'); // loves, followers, following


  useEffect(() => {
    fetchUsers();
  }, [activeTab]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      let endpoint = '';

      switch (activeTab) {
        case 'loves':
          endpoint = '/loves';
          break;
        case 'followers':
          endpoint = `/users/${currentUser?.id}/followers`;
          break;
        case 'following':
          endpoint = `/users/${currentUser?.id}/following`;
          break;
        default:
          endpoint = '/loves';
      }


      const response = await api.get(endpoint);

      if (response.data.success) {
        let usersData = response.data.data;
        
        // Handle paginated data (followers/following return paginated results)
        if (usersData.data) {
          usersData = usersData.data;
        }
        
        // Format data based on the active tab (different APIs return different structures)
        let formattedUsers = [];
        
        if (activeTab === 'loves') {
          // Loves API returns loves with other_user nested
          formattedUsers = usersData.map(love => {
            const otherUser = love.other_user;
            return {
              id: otherUser.id,
              username: otherUser.username,
              firstName: otherUser.first_name,
              lastName: otherUser.last_name,
              photoUrl: otherUser.photo_url || otherUser.profile?.photo_url || '/default-avatar.svg',
              matchDate: love.matched_at
            };
          });
        } else {
          // Followers/Following APIs return users directly with profile
          formattedUsers = usersData.map(user => ({
            id: user.id,
            username: user.username,
            firstName: user.first_name,
            lastName: user.last_name,
            photoUrl: user.profile?.photo_url || user.profile?.profile_picture || '/default-avatar.svg',
            matchDate: user.created_at
          }));
        }
        
        // Filter out current user
        formattedUsers = formattedUsers.filter(user => user.id !== currentUser?.id);



        
        setUsers(formattedUsers);
      }


    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  const handleGiftSent = () => {
    // Optionally refresh wallet data or show success message
    handleCloseModal();
  };

  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    const username = user.username?.toLowerCase() || '';
    const firstName = user.firstName?.toLowerCase() || '';
    const lastName = user.lastName?.toLowerCase() || '';
    
    return username.includes(searchLower) || 
           firstName.includes(searchLower) || 
           lastName.includes(searchLower);
  });

  const getTabIcon = (tab) => {
    switch (tab) {
      case 'loves':
        return <FaHeart />;
      case 'followers':
      case 'following':
        return <FaUserFriends />;
      default:
        return <FaGift />;
    }
  };


  const getTabLabel = (tab) => {
    switch (tab) {
      case 'loves':
        return 'Mes Loves';
      case 'followers':
        return 'Mes Followers';
      case 'following':
        return 'Mes Following';
      default:
        return '';
    }
  };


  return (
    <div className="gifts-page">
      <div className="gifts-header">
        <button className="btn-back" onClick={() => navigate(-1)}>
          <FaArrowLeft />
        </button>
        <h1>
          <FaGift className="header-icon" />
          Envoyer un cadeau
        </h1>
      </div>

      <div className="gifts-description">
        <p>Choisissez une personne à qui offrir un cadeau</p>
      </div>

      <div className="gifts-tabs">
        {['loves', 'followers', 'following'].map((tab) => (
          <button
            key={tab}
            className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {getTabIcon(tab)}
            {getTabLabel(tab)}
          </button>
        ))}
      </div>


      <div className="gifts-search">
        <FaSearch className="search-icon" />
        <input
          type="text"
          placeholder="Rechercher un utilisateur..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="gifts-content">
        {loading ? (
          <div className="gifts-loading">
            <div className="spinner"></div>
            <p>Chargement...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="gifts-empty">
            <FaGift className="empty-icon" />
            <h3>Aucun utilisateur trouvé</h3>
            <p>
              {searchTerm 
                ? 'Aucun résultat pour votre recherche'
                : `Vous n'avez pas encore de ${getTabLabel(activeTab).toLowerCase()}`
              }
            </p>
          </div>
        ) : (
          <div className="users-grid">
            {filteredUsers.map((user) => (
              <div
                key={user.id}
                className="user-card"
                onClick={() => handleUserSelect(user)}
              >
                <div className="user-avatar">
                  <img src={user.photoUrl} alt={user.username} />
                </div>
                <div className="user-info">
                  <h4 className="user-name">
                    {user.firstName && user.lastName 
                      ? `${user.firstName} ${user.lastName}`
                      : user.username
                    }
                  </h4>
                  <p className="user-username">@{user.username}</p>
                  {user.matchDate && (
                    <span className="match-date">
                      Matché le {new Date(user.matchDate).toLocaleDateString('fr-FR')}
                    </span>
                  )}
                </div>
                <div className="user-action">
                  <button className="btn-select-gift">
                    <FaGift />
                    <span>Offrir</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedUser && (
        <GiftModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          receiverId={selectedUser.id}
          receiverName={selectedUser.firstName && selectedUser.lastName 
            ? `${selectedUser.firstName} ${selectedUser.lastName}`
            : selectedUser.username
          }
        />
      )}
    </div>
  );
};

export default GiftsPage;
